import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
} from 'recharts'
import html2canvas from 'html2canvas'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { calculateYearReview } from '../lib/yearReviewCalculator'

// ─── Count-up hook ────────────────────────────────────────────────────────────

function useCountUp(target: number, duration = 1500): number {
  const [count, setCount] = useState(0)
  useEffect(() => {
    if (target === 0) { setCount(0); return }
    const start = Date.now()
    const timer = setInterval(() => {
      const elapsed  = Date.now() - start
      const progress = Math.min(elapsed / duration, 1)
      const eased    = 1 - Math.pow(1 - progress, 3)
      setCount(Math.round(target * eased))
      if (progress === 1) clearInterval(timer)
    }, 16)
    return () => clearInterval(timer)
  }, [target, duration])
  return count
}

// ─── Category labels ──────────────────────────────────────────────────────────

const CAT_LABELS: Record<string, string> = {
  produce:       'Produce',
  dairy:         'Dairy',
  meat:          'Meat',
  bakery:        'Bakery',
  pantry:        'Pantry',
  cleaning:      'Cleaning',
  personal_care: 'Personal Care',
  other:         'Other',
}

const CAT_COLORS: Record<string, string> = {
  produce:       '#6B9E78',
  dairy:         '#4A90D9',
  meat:          '#C25B4E',
  bakery:        '#D4863A',
  pantry:        '#9A9491',
  cleaning:      '#7B68EE',
  personal_care: '#D4A843',
  other:         '#C5BFB8',
}

// ─── Stat card ────────────────────────────────────────────────────────────────

function StatCard({
  index, bg, numberColor, value, label, prefix = '', suffix = '', zeroText,
}: {
  index: number; bg: string; numberColor: string; value: number
  label: string; prefix?: string; suffix?: string; zeroText?: string
}) {
  const animated = useCountUp(value)
  return (
    <motion.div
      className="w-full rounded-2xl p-6 mb-3"
      style={{ background: bg }}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.08 }}
    >
      {value === 0 && zeroText ? (
        <p className="text-2xl font-bold" style={{ color: numberColor }}>{zeroText}</p>
      ) : (
        <p className="text-5xl font-bold" style={{ color: numberColor }}>
          {prefix}{animated.toLocaleString()}{suffix}
        </p>
      )}
      <p className="text-sm text-[#7A7468] mt-1">{label}</p>
    </motion.div>
  )
}

// ─── YearReview ───────────────────────────────────────────────────────────────

export default function YearReview() {
  const { user }    = useAuth()
  const navigate    = useNavigate()
  const shareCardRef = useRef<HTMLDivElement>(null)
  const currentYear  = new Date().getFullYear()
  const [selectedYear, setSelectedYear] = useState(currentYear)
  const [sharing, setSharing]           = useState(false)

  useEffect(() => { document.title = 'Year in Review — Pantry' }, [])

  // ── ALL hooks must be called before any return ────────────────────────────

  const { data: userData, isLoading: userLoading } = useQuery({
    queryKey: ['user-premium', user?.id],
    queryFn:  async () => {
      if (!user?.id) return null
      const { data } = await supabase
        .from('users')
        .select('is_premium, household_id')
        .eq('id', user.id)
        .single()
      return data
    },
    enabled:   !!user?.id,
    staleTime: 30000,
  })

  const isPremium   = userData?.is_premium   ?? false
  const householdId = userData?.household_id ?? null

  const { data: reviewData, isLoading: reviewLoading } = useQuery({
    queryKey: ['year-review', householdId, selectedYear],
    queryFn:  async () => {
      if (!householdId) return null
      return calculateYearReview(householdId, supabase, selectedYear)
    },
    enabled: !!householdId && isPremium,
  })

  // ── Share handler (plain function, not a hook) ────────────────────────────

  const handleShare = async () => {
    if (!shareCardRef.current || !reviewData) return
    setSharing(true)
    try {
      const canvas = await html2canvas(shareCardRef.current, {
        backgroundColor: null,
        scale: 2,
      })
      canvas.toBlob(async (blob) => {
        if (!blob) return
        if (navigator.share && navigator.canShare) {
          const file = new File([blob], 'pantry-year-review.png', { type: 'image/png' })
          if (navigator.canShare({ files: [file] })) {
            await navigator.share({
              files: [file],
              title: `My ${reviewData.year} Pantry Year`,
              text:  `I tracked ${reviewData.totalItemsTracked} items and saved $${reviewData.totalSavedDollars.toFixed(2)} on groceries this year with Pantry`,
            })
            return
          }
        }
        const url = URL.createObjectURL(blob)
        const a   = document.createElement('a')
        a.href     = url
        a.download = `pantry-${reviewData.year}-review.png`
        a.click()
        URL.revokeObjectURL(url)
      })
    } catch (err) {
      console.error('Share error:', err)
    } finally {
      setSharing(false)
    }
  }

  // ── Conditional rendering — all hooks already called above ───────────────

  // Still loading auth or user row
  if (!user || userLoading) {
    return (
      <div className="min-h-screen bg-[#FAF8F4] flex items-center justify-center">
        <div className="w-8 h-8 rounded-full border-2 border-[#6B9E78] border-t-transparent animate-spin" />
      </div>
    )
  }

  // Not Pantry + — redirect
  if (!isPremium) {
    navigate('/paywall')
    return null
  }

  // Review data still loading
  if (reviewLoading || !reviewData) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4"
           style={{ background: '#FAF8F4' }}>
        <div className="w-10 h-10 rounded-full border-2 border-[#6B9E78] border-t-transparent animate-spin" />
        <p className="text-sm text-[#9A9491]">Building your year…</p>
      </div>
    )
  }

  // Not enough data yet
  if (reviewData.totalConfirmations < 10) {
    return (
      <div className="min-h-screen" style={{ background: '#FAF8F4' }}>
        <div className="px-4 pt-8 pb-4 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="w-8 h-8 flex items-center justify-center rounded-full
                       bg-[#F0EDE8] text-[#7A7468] text-sm"
          >←</button>
          <h1 className="text-xl font-semibold text-[#2C2A26]">Year in Review</h1>
        </div>
        <div className="text-center py-12 px-8">
          <p className="text-5xl mb-4">🌱</p>
          <p className="text-lg font-bold text-[#2C2A26]">Your year is just getting started</p>
          <p className="text-sm text-[#7A7468] mt-2 max-w-[280px] mx-auto">
            Keep tracking your pantry and come back at the end of the year to see your story
          </p>
        </div>
      </div>
    )
  }

  const maxCategoryCount = Math.max(...reviewData.topCategories.map(c => c.count), 1)

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen pb-32" style={{ background: '#FAF8F4' }}>

      {/* Header */}
      <div className="px-4 pt-8 pb-4 flex items-center justify-between">
        <button
          onClick={() => navigate(-1)}
          className="w-8 h-8 flex items-center justify-center rounded-full
                     bg-[#F0EDE8] text-[#7A7468] text-sm"
        >←</button>
        <h1 className="text-xl font-semibold text-[#2C2A26]">Year in Review</h1>
        <button
          onClick={handleShare}
          disabled={sharing}
          className="w-8 h-8 flex items-center justify-center rounded-full
                     bg-[#F0EDE8] text-[#7A7468] text-sm disabled:opacity-50"
          aria-label="Share"
        >
          {sharing ? (
            <span className="w-4 h-4 rounded-full border-2 border-[#7A7468] border-t-transparent animate-spin" />
          ) : (
            <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
              <path d="M15 7a2 2 0 1 0 0-4 2 2 0 0 0 0 4ZM5 12a2 2 0 1 0 0-4 2 2 0 0 0 0 4ZM15 17a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z"
                stroke="currentColor" strokeWidth="1.5" />
              <path d="M7 11l6 3M13 6L7 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          )}
        </button>
      </div>

      <div className="px-4 max-w-[480px] mx-auto">

        {/* Hero */}
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <motion.span
            className="text-6xl mb-4 block"
            animate={{ y: [0, -10, 0] }}
            transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}
          >
            🎉
          </motion.span>
          <h2 className="text-2xl font-bold text-[#2C2A26]">
            Your {reviewData.periodLabel === 'Last 12 months'
              ? 'Last 12 Months'
              : `${reviewData.year} Pantry Year`}
          </h2>
          <p className="text-base text-[#7A7468] mt-2">Here is how your household did</p>
        </div>

        {/* Year selector */}
        <div className="flex gap-2 justify-center mb-6">
          {[currentYear - 1, currentYear].map(year => (
            <button
              key={year}
              onClick={() => setSelectedYear(year)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                selectedYear === year
                  ? 'bg-[#6B9E78] text-white'
                  : 'bg-[#F2EDE6] text-[#7A7468]'
              }`}
            >
              {year}
            </button>
          ))}
        </div>

        {/* Stat cards */}
        <StatCard index={0} bg="#E8F2EA" numberColor="#6B9E78"
          value={reviewData.totalItemsTracked} label="items tracked this year" />

        <StatCard index={1} bg="#EDE8FF" numberColor="#7B68EE"
          value={reviewData.totalShops} label="shopping trips completed" />

        <StatCard index={2} bg="#FDF0E3" numberColor="#D4863A"
          value={Math.round(reviewData.totalSpent)} label="spent on groceries" prefix="$" />

        <StatCard index={3} bg="#E8F2EA" numberColor="#6B9E78"
          value={reviewData.totalSavedDollars}
          label="saved by using items before expiry"
          prefix="$"
          zeroText="We are just getting started" />

        <motion.div
          className="w-full rounded-2xl p-6 mb-3"
          style={{ background: '#FAEAE8' }}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 4 * 0.08 }}
        >
          {reviewData.totalWastedDollars === 0 ? (
            <>
              <p className="text-3xl font-bold text-[#6B9E78]">🌱 ZERO WASTE</p>
              <p className="text-sm text-[#7A7468] mt-1">No food went to waste this year</p>
            </>
          ) : (
            <>
              <p className="text-5xl font-bold text-[#C25B4E]">
                ${Math.round(reviewData.totalWastedDollars).toLocaleString()}
              </p>
              <p className="text-sm text-[#7A7468] mt-1">in food that went to waste</p>
            </>
          )}
        </motion.div>

        {/* Monthly spend chart */}
        {reviewData.monthlySpend.length > 0 && (
          <motion.div
            className="bg-white rounded-2xl border border-[#E8E2DC] p-5 mb-3"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <p className="text-sm font-semibold text-[#2C2A26] mb-4">
              Your spending month by month
            </p>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={reviewData.monthlySpend} margin={{ top: 4, right: 0, left: -20, bottom: 0 }}>
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#9A9491' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#9A9491' }} axisLine={false} tickLine={false}
                  tickFormatter={v => `$${v}`} />
                <Tooltip
                  formatter={(v: unknown) => [`$${(v as number).toFixed(2)}`, 'Spent']}
                  contentStyle={{ borderRadius: 8, border: '1px solid #E8E2DC', fontSize: 12 }}
                />
                <Bar dataKey="spent" radius={[4, 4, 0, 0]} fill="#6B9E78" label={false} />
              </BarChart>
            </ResponsiveContainer>
            {reviewData.bestMonth && (
              <p className="text-xs text-[#7A7468] mt-2 text-center">
                Best month: <span className="font-semibold text-[#6B9E78]">{reviewData.bestMonth}</span>
                {reviewData.worstMonth && reviewData.worstMonth !== reviewData.bestMonth && (
                  <> · Highest spend: <span className="font-semibold text-[#D4863A]">{reviewData.worstMonth}</span></>
                )}
              </p>
            )}
          </motion.div>
        )}

        {/* Highlights */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mb-3"
        >
          <p className="text-sm font-semibold text-[#7A7468] uppercase tracking-wide mb-3">
            Your highlights
          </p>
          <div className="flex flex-col gap-3">

            {/* Most reliable item */}
            <div className="bg-white rounded-2xl p-4 border-2 border-[#D4863A]">
              {reviewData.mostReliableItem ? (
                <>
                  <p className="text-4xl text-center mb-2">{reviewData.mostReliableItem.emoji}</p>
                  <p className="text-sm font-semibold text-[#2C2A26] text-center">
                    {reviewData.mostReliableItem.name}
                  </p>
                  <p className="text-xs text-[#7A7468] text-center mt-1">
                    Confirmed {reviewData.mostReliableItem.streak} times · Never wasted
                  </p>
                  <p className="text-xs text-center text-[#D4863A] font-semibold mt-1">
                    Most reliable item ⭐
                  </p>
                </>
              ) : (
                <p className="text-xs text-[#7A7468] text-center py-2">
                  Start tracking to find your most reliable item
                </p>
              )}
            </div>

            {/* Best month */}
            {reviewData.bestMonth && (
              <div className="bg-white rounded-2xl p-4 border-2 border-[#6B9E78]">
                <p className="text-4xl text-center mb-2">📅</p>
                <p className="text-2xl font-bold text-[#2C2A26] text-center">
                  {reviewData.bestMonth}
                </p>
                <p className="text-xs text-[#7A7468] text-center mt-1">Lowest grocery spend</p>
              </div>
            )}

          </div>
        </motion.div>

        {/* Most wasted */}
        {reviewData.mostWastedItem && reviewData.mostWastedItem.wastedDollars > 0 && (
          <motion.div
            className="bg-white rounded-2xl border border-[#E8E2DC] p-5 mb-3"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <p className="text-sm font-semibold text-[#7A7468] uppercase tracking-wide mb-1">
              Room to improve
            </p>
            <p className="text-xs text-[#B0A99F] mb-4">
              This item cost you the most in waste
            </p>
            <p className="text-4xl text-center mb-2">{reviewData.mostWastedItem.emoji}</p>
            <p className="text-base font-bold text-[#2C2A26] text-center">
              {reviewData.mostWastedItem.name}
            </p>
            <p className="text-2xl font-bold text-[#C25B4E] text-center mt-1">
              ${reviewData.mostWastedItem.wastedDollars.toFixed(2)} wasted
            </p>
            <div className="mt-3 bg-[#FAEAE8] rounded-xl p-3">
              <p className="text-xs text-[#7A7468]">
                💡 Try buying less at a time or adding an expiry date to track it
              </p>
            </div>
          </motion.div>
        )}

        {/* Top categories */}
        {reviewData.topCategories.length > 0 && (
          <motion.div
            className="bg-white rounded-2xl border border-[#E8E2DC] p-5 mb-3"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <p className="text-sm font-semibold text-[#7A7468] uppercase tracking-wide mb-4">
              What you bought most
            </p>
            <div className="flex flex-col gap-3">
              {reviewData.topCategories.map(cat => {
                const pct   = (cat.count / maxCategoryCount) * 100
                const color = CAT_COLORS[cat.category] ?? '#C5BFB8'
                const label = CAT_LABELS[cat.category]  ?? cat.category
                return (
                  <div key={cat.category}>
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-xs font-medium text-[#2C2A26]">{label}</span>
                      <span className="text-xs text-[#9A9491]">
                        {cat.count} {cat.spent > 0 ? `· $${cat.spent.toFixed(0)}` : ''}
                      </span>
                    </div>
                    <div className="h-2 rounded-full overflow-hidden" style={{ background: '#F0EDE8' }}>
                      <motion.div
                        className="h-full rounded-full"
                        style={{ background: color }}
                        initial={{ width: 0 }}
                        whileInView={{ width: `${pct}%` }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, ease: 'easeOut' }}
                      />
                    </div>
                  </div>
                )
              })}
            </div>
          </motion.div>
        )}

        {/* Share section */}
        <motion.div
          className="rounded-2xl p-6 mb-3 text-white text-center"
          style={{ background: 'linear-gradient(135deg, #6B9E78, #4A7C59)' }}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <p className="text-xl font-bold mb-1">🏠 Pantry</p>
          <p className="text-sm opacity-80 mb-1">Your {reviewData.year} in groceries</p>
          <p className="text-xs opacity-70 mb-4">
            {reviewData.totalItemsTracked} items ·
            ${reviewData.totalSavedDollars.toFixed(0)} saved ·
            {reviewData.totalShops} shops
          </p>
          <button
            onClick={handleShare}
            disabled={sharing}
            className="px-6 py-2.5 rounded-xl text-sm font-semibold bg-white text-[#6B9E78]
                       disabled:opacity-60 transition-opacity"
          >
            {sharing ? 'Preparing…' : 'Share my year'}
          </button>
        </motion.div>

      </div>

      {/* Hidden share card for html2canvas */}
      <div
        ref={shareCardRef}
        style={{
          position:     'absolute',
          left:         '-9999px',
          width:        '400px',
          padding:      '32px',
          background:   'linear-gradient(135deg, #6B9E78, #4A7C59)',
          borderRadius: '24px',
          color:        'white',
          fontFamily:   'sans-serif',
        }}
      >
        <p style={{ fontSize: '24px', fontWeight: 700, margin: '0 0 4px 0' }}>🏠 Pantry</p>
        <p style={{ fontSize: '14px', opacity: 0.8, margin: '0 0 24px 0' }}>
          {reviewData.year} Year in Review
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
          <div style={{ background: 'rgba(255,255,255,0.15)', borderRadius: '16px', padding: '16px' }}>
            <p style={{ fontSize: '32px', fontWeight: 700, margin: 0 }}>
              {reviewData.totalItemsTracked}
            </p>
            <p style={{ fontSize: '12px', opacity: 0.8, margin: '4px 0 0 0' }}>items tracked</p>
          </div>
          <div style={{ background: 'rgba(255,255,255,0.15)', borderRadius: '16px', padding: '16px' }}>
            <p style={{ fontSize: '32px', fontWeight: 700, margin: 0 }}>
              ${reviewData.totalSavedDollars.toFixed(0)}
            </p>
            <p style={{ fontSize: '12px', opacity: 0.8, margin: '4px 0 0 0' }}>saved</p>
          </div>
          <div style={{ background: 'rgba(255,255,255,0.15)', borderRadius: '16px', padding: '16px' }}>
            <p style={{ fontSize: '32px', fontWeight: 700, margin: 0 }}>{reviewData.totalShops}</p>
            <p style={{ fontSize: '12px', opacity: 0.8, margin: '4px 0 0 0' }}>shopping trips</p>
          </div>
        </div>
        <p style={{ fontSize: '12px', opacity: 0.6, textAlign: 'center', margin: 0 }}>
          pantryioapp.com
        </p>
      </div>

    </div>
  )
}
